import { useQuery, useQueryClient } from '@tanstack/react-query';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { GetSiteSettingsMetadataService } from '~src/settings/site-settings/restApi/requests/GetSiteSettingsMetadata';
import { ISiteSettingsMetadataBySections, ISiteSettingsSection } from '~src/settings/site-settings/restApi/types/ISiteSettingsMetadata';

// Sections that are always requested regardless of feature flag status.
export const BASE_SITE_SETTINGS_SECTIONS = [
    'collections',
    'recycleBin',
    'namedSharingEnabled',
    'dataAlertsEnabled',
    'groupSetsEnabled',
    'commenting',
    'subscriptionAndDDAEmailLinksEnabled',
    'progressDialog'
] as const;

// We use a thunk to read the feature flag value so that it is fresh at every read,
// rather than cached at module load.
const FEATURE_FLAGGED_SITE_SETTINGS_SECTIONS: Record<string, () => boolean> = {
    hybridSearchEnabled: () => FeatureFlags.ces_HybridSearchQueryEnabled
};

// Merge the base (always-enabled) site settings sections with the ones
// gated behind a feature flag. This ensures we don't send a monolith
// request for unsupported sections, which would result in a failed request.
export function getSiteSettingsSections(): string[] {
    const sections: string[] = [...BASE_SITE_SETTINGS_SECTIONS];

    for (const [section, isEnabled] of Object.entries(FEATURE_FLAGGED_SITE_SETTINGS_SECTIONS)) {
        if (isEnabled()) {
            sections.push(section);
        }
    }

    return sections;
}

// Master query key for fetching all sections
const ALL_SECTIONS_QUERY_KEY = ['siteSettingsMetadata', 'all'];

// Helper to get query key for a specific section
const getSectionQueryKey = (sectionName: string) => ['siteSettingsMetadata', sectionName];

/**
 * Hook to get site settings metadata for a specific section
 * 
 * Strategy:
 * - First component to mount triggers ONE API call for all sections
 * - Splits the response and caches each section individually
 * - Subsequent components read from their section-specific cache
 * 
 * Benefits:
 * - Single API call (efficient)
 * - Per-section cache entries (granular invalidation)
 * - Components get exactly what they need
 * 
 * Only fetches when:
 * 1. metadata-driven settings are enabled
 * 2. this section's metadata is not already cached
 * 
 * @param sectionName - The name of the section (e.g., 'collections', 'recycleBin')
 * @returns Object with sectionMetadata for the requested section, isLoading, and error
 */
export function useSiteSettingsMetadata(sectionName: string) {
    const queryClient = useQueryClient();

    const { data: sectionData, isLoading: sectionLoading } = useQuery({
        queryKey: getSectionQueryKey(sectionName),
        queryFn: () => {
            return Promise.resolve(undefined as ISiteSettingsSection | undefined);
        },
        staleTime: Infinity,
        enabled: false // Never actually fetch, just read from cache
    });

    const { isLoading: allLoading, error } = useAllSiteSettingsMetadata();

    return {
        sectionMetadata: sectionData,
        isLoading: sectionLoading || allLoading,
        error
    };
}

/**
 * Fetch (once) and subscribe to the full V2 site settings metadata response.
 */
export function useAllSiteSettingsMetadata() {
    const queryClient = useQueryClient();

    const { data, isLoading, error } = useQuery({
        queryKey: ALL_SECTIONS_QUERY_KEY,
        queryFn: async (): Promise<ISiteSettingsMetadataBySections> => {
            const response = await GetSiteSettingsMetadataService.getSectionLevelMetadata(
                getSiteSettingsSections()
            );

            response.sectionMetadata.forEach(section => {
                queryClient.setQueryData(getSectionQueryKey(section.name), section);
            });

            return response;
        },
        staleTime: Infinity,
        enabled: FeatureFlags.metadataDrivenSiteSettingsEnabled
    });

    return {
        allMetadata: data,
        isLoading,
        error
    };
}

